import { RustCourse } from './types';

export const RUST_COURSES: RustCourse[] = [
  {
    id: 'rust-fundamentals',
    title: 'Rust Fundamentals & Systems Programming',
    description: 'Master Rust from the ground up. Learn memory safety, ownership, and systems programming concepts that make Rust unique.',
    level: 'beginner',
    totalDuration: '8 hours',
    image: '/api/placeholder/400/300',
    tags: ['Rust', 'Systems Programming', 'Memory Safety', 'Performance'],
    prerequisites: ['Basic programming experience in any language'],
    estimatedHours: 8,
    objectives: [
      'Understand Rust\'s ownership and borrowing system',
      'Write safe and efficient systems-level code',
      'Master Rust\'s type system and pattern matching',
      'Handle errors using Result and Option types'
    ],
    learningOutcomes: [
      'Install and configure Rust development environment',
      'Understand ownership, borrowing, and lifetimes',
      'Write memory-safe code without garbage collection',
      'Use pattern matching and enums effectively',
      'Handle errors gracefully with Result and Option',
      'Build command-line applications in Rust'
    ],
    modules: [
      {
        id: 'rust-intro',
        title: 'Introduction to Rust',
        description: 'Learn why Rust exists and what makes it special',
        content: 'Rust is a systems programming language that combines memory safety with performance...',
        duration: '1 hour',
        difficulty: 'beginner',
        order: 1,
        estimatedTime: '1 hour',
        learningObjectives: [
          'Understand Rust\'s design philosophy',
          'Install Rust and Cargo',
          'Write your first Rust program'
        ],
        codeExamples: [
          {
            title: 'Hello World',
            code: 'fn main() {\n    println!("Hello, world!");\n}',
            explanation: 'A simple Rust program that prints "Hello, world!" to the console.',
            language: 'rust'
          }
        ]
      },
      {
        id: 'rust-ownership',
        title: 'Ownership and Borrowing',
        description: 'Master Rust\'s unique ownership system',
        content: 'Ownership is Rust\'s most unique feature, enabling memory safety without garbage collection...',
        duration: '2 hours',
        difficulty: 'intermediate',
        order: 2,
        estimatedTime: '2 hours',
        learningObjectives: [
          'Understand ownership rules',
          'Learn borrowing and references',
          'Master mutable and immutable references'
        ]
      },
      {
        id: 'rust-structs-enums',
        title: 'Structs and Enums',
        description: 'Define custom data types with structs and enums',
        content: 'Rust\'s structs and enums provide powerful ways to model your data...',
        duration: '2 hours',
        difficulty: 'intermediate',
        order: 3,
        estimatedTime: '2 hours',
        learningObjectives: [
          'Define and use structs',
          'Create and match enums',
          'Implement methods on types'
        ]
      },
      {
        id: 'rust-error-handling',
        title: 'Error Handling',
        description: 'Handle errors safely with Result and Option',
        content: 'Rust uses Result and Option types for safe error handling without exceptions...',
        duration: '1.5 hours',
        difficulty: 'intermediate',
        order: 4,
        estimatedTime: '1.5 hours',
        learningObjectives: [
          'Use Result for recoverable errors',
          'Handle Options for nullable values',
          'Chain operations with ? operator'
        ]
      },
      {
        id: 'rust-collections',
        title: 'Collections and Iterators',
        description: 'Work with vectors, hashmaps, and iterators',
        content: 'Rust provides powerful collections and iterator patterns for data processing...',
        duration: '1.5 hours',
        difficulty: 'intermediate',
        order: 5,
        estimatedTime: '1.5 hours',
        learningObjectives: [
          'Use vectors and hashmaps',
          'Master iterator patterns',
          'Perform functional programming operations'
        ]
      }
    ],
    type: 'rust'
  },
  {
    id: 'rust-web-development',
    title: 'Rust for Web Development (Axum, Actix)',
    description: 'Build high-performance web applications and APIs using modern Rust web frameworks.',
    level: 'intermediate',
    totalDuration: '10 hours',
    image: '/api/placeholder/400/300',
    tags: ['Rust', 'Web Development', 'Axum', 'Actix', 'REST API'],
    prerequisites: ['Rust Fundamentals', 'Basic web development knowledge'],
    estimatedHours: 10,
    objectives: [
      'Build REST APIs with Axum framework',
      'Implement authentication and middleware',
      'Connect to databases with SQLx',
      'Deploy Rust web applications'
    ],
    learningOutcomes: [
      'Create REST APIs with Axum',
      'Handle HTTP requests and responses',
      'Implement middleware and authentication',
      'Connect to PostgreSQL with SQLx',
      'Build real-time features with WebSockets',
      'Deploy to cloud platforms'
    ],
    modules: [
      {
        id: 'axum-basics',
        title: 'Getting Started with Axum',
        description: 'Learn the basics of the Axum web framework',
        content: 'Axum is a modern, ergonomic web framework for Rust...',
        duration: '2 hours',
        difficulty: 'intermediate',
        order: 1
      },
      {
        id: 'rust-routing-handlers',
        title: 'Routing and Handlers',
        description: 'Define routes and handle HTTP requests',
        content: 'Learn to create flexible routing systems and request handlers...',
        duration: '2 hours',
        difficulty: 'intermediate',
        order: 2
      },
      {
        id: 'rust-middleware',
        title: 'Middleware and Authentication',
        description: 'Implement middleware and secure your APIs',
        content: 'Add cross-cutting concerns with middleware layers...',
        duration: '2.5 hours',
        difficulty: 'advanced',
        order: 3
      },
      {
        id: 'rust-database',
        title: 'Database Integration with SQLx',
        description: 'Connect your Rust app to databases',
        content: 'Use SQLx for compile-time checked SQL queries...',
        duration: '2 hours',
        difficulty: 'intermediate',
        order: 4
      },
      {
        id: 'rust-websockets',
        title: 'Real-time Features with WebSockets',
        description: 'Build real-time applications',
        content: 'Implement WebSocket connections for real-time communication...',
        duration: '1.5 hours',
        difficulty: 'advanced',
        order: 5
      }
    ],
    type: 'rust'
  },
  {
    id: 'rust-devops-cli',
    title: 'Rust for DevOps Tools & CLI Apps',
    description: 'Create powerful command-line tools and DevOps utilities with Rust\'s performance and reliability.',
    level: 'intermediate',
    totalDuration: '6 hours',
    image: '/api/placeholder/400/300',
    tags: ['Rust', 'DevOps', 'CLI', 'Tools', 'Automation'],
    prerequisites: ['Rust Fundamentals', 'Command line experience'],
    estimatedHours: 6,
    objectives: [
      'Build CLI applications with clap',
      'Process files and directories efficiently',
      'Create DevOps automation tools',
      'Package and distribute Rust binaries'
    ],
    learningOutcomes: [
      'Create command-line interfaces with clap',
      'Handle file I/O and directory traversal',
      'Build system monitoring tools',
      'Parse and generate various file formats',
      'Create cross-platform binaries',
      'Integrate with existing DevOps pipelines'
    ],
    modules: [
      {
        id: 'rust-cli-basics',
        title: 'CLI Fundamentals with Clap',
        description: 'Build command-line interfaces',
        content: 'Learn to create professional CLI tools with argument parsing...',
        duration: '1.5 hours',
        difficulty: 'intermediate',
        order: 1
      },
      {
        id: 'rust-file-processing',
        title: 'File and Directory Processing',
        description: 'Handle files and directories efficiently',
        content: 'Master file I/O, directory traversal, and path manipulation...',
        duration: '1.5 hours',
        difficulty: 'intermediate',
        order: 2
      },
      {
        id: 'rust-system-tools',
        title: 'System Monitoring Tools',
        description: 'Build tools for system monitoring',
        content: 'Create utilities for monitoring system resources and processes...',
        duration: '2 hours',
        difficulty: 'advanced',
        order: 3
      },
      {
        id: 'rust-deployment',
        title: 'Packaging and Distribution',
        description: 'Package and distribute your tools',
        content: 'Learn to create releases, cross-compile, and distribute binaries...',
        duration: '1 hour',
        difficulty: 'intermediate',
        order: 4
      }
    ],
    type: 'rust'
  },
  {
    id: 'advanced-rust',
    title: 'Advanced Rust: Memory Management & Performance',
    description: 'Master advanced Rust concepts including unsafe code, custom allocators, and performance optimization.',
    level: 'advanced',
    totalDuration: '12 hours',
    image: '/api/placeholder/400/300',
    tags: ['Rust', 'Advanced', 'Performance', 'Unsafe', 'Memory Management'],
    prerequisites: ['Solid Rust fundamentals', 'Systems programming experience'],
    estimatedHours: 12,
    objectives: [
      'Understand unsafe Rust and when to use it',
      'Optimize code for maximum performance',
      'Implement custom allocators',
      'Master advanced trait patterns'
    ],
    learningOutcomes: [
      'Write safe unsafe code when necessary',
      'Profile and optimize Rust applications',
      'Implement zero-cost abstractions',
      'Use advanced trait patterns like GATs',
      'Build custom allocators',
      'Integrate with C libraries safely'
    ],
    modules: [
      {
        id: 'unsafe-rust',
        title: 'Unsafe Rust and Raw Pointers',
        description: 'Learn when and how to use unsafe code',
        content: 'Understand the safety guarantees you give up with unsafe...',
        duration: '3 hours',
        difficulty: 'advanced',
        order: 1
      },
      {
        id: 'rust-performance',
        title: 'Performance Optimization',
        description: 'Profile and optimize Rust code',
        content: 'Learn techniques for measuring and improving performance...',
        duration: '3 hours',
        difficulty: 'advanced',
        order: 2
      },
      {
        id: 'advanced-traits',
        title: 'Advanced Trait Patterns',
        description: 'Master complex trait usage',
        content: 'Explore GATs, HKTs, and other advanced trait patterns...',
        duration: '3 hours',
        difficulty: 'expert',
        order: 3
      },
      {
        id: 'custom-allocators',
        title: 'Custom Allocators and No-Std',
        description: 'Build for embedded and constrained environments',
        content: 'Learn to work without the standard library...',
        duration: '3 hours',
        difficulty: 'expert',
        order: 4
      }
    ],
    type: 'rust'
  }
];